import { isBrowser } from '@/helpers/BrowserHelper';
import React, { useCallback, useEffect, VFC } from 'react';
import { useState } from 'react';
import { useRef } from 'react';

const SDK_URL = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${process.env.NAVER_MAP_CLIENT_ID}`;
const SDK_SCRIPT_ID = `naver-map-sdk`;

// 네이버지도 좌표
interface NaverMapLatLngProps {
  lat: number;
  lng: number;
}

// 네이버지도 렌더링 파라미터
interface NaverMapRanderProps {
  LatLng: NaverMapLatLngProps;
  zoom?: number;
}

// 네이버지도 마커 파라미터
interface NaverMapMarkerProps {
  LatLng: NaverMapLatLngProps;
  icon?: {
    url: any;
    size?: { width: number; height: number };
    scaledSize?: { width: number; height: number };
    anchor?: { x: number; y: number };
  };
}

interface NaverMapProps {
  containerProp?: React.CSSProperties;
  mapProps: NaverMapRanderProps;
  markerProps?: NaverMapMarkerProps;
}

declare global {
  interface naver {}
}

/**
 * 네이버지도
 */
const NaverMap: VFC<NaverMapProps> = (
  { containerProp, mapProps, markerProps },
  props,
) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const divRef = useRef(null);
  const mapId = `naverMap-${
    Date.now().toString(36) + Math.random().toString(36).substr(2)
  }`;

  const loadMap = useCallback(() => {
    const map = new naver.maps.Map(divRef?.current?.id || '', {
      center: new naver.maps.LatLng(mapProps.LatLng.lat, mapProps.LatLng.lng),
      zoom: mapProps.zoom || 16,
      /*
      minZoom: 15,
      // 확대 시 타일 변경되는 시간
      tileDuration: 300,
      // 타일 투명도 ( 투명도 낮추면 배경 색이 보임 )
      baseTileOpacity: 1,
      //배경 색
      background: 'white',
      //화면 바깥 여분 타일 개수
      tileSpare: 7,
      */
    });
    if (markerProps) {
      const marker = new naver.maps.Marker({
        map: map,
        position: new naver.maps.LatLng(
          markerProps.LatLng.lat,
          markerProps.LatLng.lng,
        ),
        icon: markerProps.icon,
      });
    }
  }, [mapProps, markerProps]);

  // 스크립트 로드
  const loadScript = useCallback(() => {
    setIsLoading(true);
    const callback = () => {
      setIsLoaded(true);
      loadMap();
    };
    if (document.querySelectorAll(`#${SDK_SCRIPT_ID}`).length === 0) {
      const script = document.createElement(`script`);
      script.id = SDK_SCRIPT_ID;
      script.src = SDK_URL;
      script.onload = callback;
      script.async = true;
      document.head.appendChild(script);
    } else {
      // script가 이미 로드된 경우, callback만 실행
      try {
        callback();
      } catch {
        // 한페이지에서 이 컴포넌트를 여려번 사용시 naver 객체가 아직 준비되지 않은 경우가 있어 재귀호출로 대기
        setTimeout(callback, 500);
      }
    }
  }, [loadMap]);

  useEffect(() => {
    // div가 렌더링된 후 SDK 로딩
    if (divRef.current && !isLoaded && !isLoading) {
      loadScript();
    }
  }, [isLoaded, isLoading]);

  if (!isBrowser) {
    return null;
  }
  return (
    <div style={{ ...containerProp?.style }}>
      <div
        id={mapId}
        ref={divRef}
        style={{ width: '100%', height: '100%' }}
      ></div>
    </div>
  );
};

export default NaverMap;
